import { useState, useEffect, memo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import Text from '../../../../shared/Text';

import { NotificationsContainer } from './PostNotifications/NotificationsContainer.jsx';
import { TileCard } from '../Tiles/TileCard.jsx';
import { PostMenu } from '../Posts/PostMenu.jsx';

import { parseDate, timeOptions } from '../../utils/helpers';
import { setScrollPosition } from '../../../../lib/redux/campaignScroll/campaignScrollSlice';
import { BadgeList } from './Summary/BadgeList.jsx';
import { useChangePostDate } from './hooks/useChangePostDate.jsx';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

import { ERROR_TIMER, LIVE } from '../../utils/constants';

const TilesArea = ({ children, uuid, tiles }) => {
  const { isOver, setNodeRef, over, active } = useDroppable({
    id: `post-${uuid}`,
  });

  const isOverTilesContainer = over
    ? (uuid === over.id && active?.data.current?.type !== 'container') ||
      tiles.some(tile => tile.id === over.id)
    : false;

  return (
    <Tiles isOver={isOverTilesContainer || isOver} ref={setNodeRef}>
      {children}
    </Tiles>
  );
};

export const Post = memo(({ post, campaignId, campaignEndDate, tiles }) => {
  const { t } = useTranslation('posts');
  const history = useHistory();
  const dispatch = useDispatch();
  const { campaign } = useCampaignContext();
  const isDisabled = campaign.isArchived || !!campaign.deletedAt;
  const [showIncompleteBorder, setShowIncompleteBorder] = useState(false);
  const [noDateError, setNoDateError] = useState(false);
  const { uuid, publishDate, status, sendHour, boardTimezone } = post;

  const { openChangeDateModal, ChangePostDateModal } = useChangePostDate();
  const parsedTimes = timeOptions(boardTimezone);

  const newTilePosition = tiles.length + 1;
  const livePost = post.status === LIVE && post.publishDate;
  const lockedToolTip = livePost ? t('postAlreadyLive') : null;
  const postDateClick = () => {
    if (livePost || isDisabled) return;

    openChangeDateModal({ post, campaignId });
  };

  useEffect(() => {
    if (noDateError) {
      const timer = setTimeout(() => setNoDateError(false), ERROR_TIMER);
      return () => clearTimeout(timer);
    }

    return undefined;
  });

  useEffect(() => {
    if (showIncompleteBorder) {
      const timer = setTimeout(
        () => setShowIncompleteBorder(false),
        ERROR_TIMER,
      );
      return () => clearTimeout(timer);
    }

    return undefined;
  });

  return (
    <Container>
      <ChangePostDateModal />
      <Header>
        <DateStatusContainer>
          <PostDate
            onClick={postDateClick}
            $error={noDateError}
            data-tip={lockedToolTip}
            data-for="tooltip-live-post"
          >
            <PostText $error={noDateError}>{t('postDate')}</PostText>
            <TileDate>
              {parseDate(publishDate, t('postSummary.addDate'), 'iii M/d')}
            </TileDate>
            <PostText>
              {sendHour !== null &&
                sendHour !== undefined &&
                parsedTimes.find(option => option.value === post.sendHour)
                  .label}
            </PostText>
            <CalendarIcon />
          </PostDate>
          <PostStatus>
            <TileCount>
              {t('postSummary.tileCount', { count: tiles.length })}
            </TileCount>
            <BadgeList status={status} />
            {!isDisabled && <PostMenu campaignId={campaignId} postId={uuid} />}
          </PostStatus>
        </DateStatusContainer>
        {noDateError && (
          <ErrorContainer>
            <ErrorText>{t('noPostDate')}</ErrorText>
          </ErrorContainer>
        )}
      </Header>
      <NotificationsContainer
        post={post}
        tiles={tiles}
        campaignId={campaignId}
        campaignEndDate={campaignEndDate}
        setShowIncompleteBorder={setShowIncompleteBorder}
        setNoDateError={setNoDateError}
      />
      <SortableContext items={tiles}>
        <TilesArea uuid={uuid} tiles={tiles}>
          {tiles.map((tile, index) => (
            <TileCard
              key={tile.id}
              tile={{ ...tile, index }}
              index={index}
              campaignId={campaignId}
              showIncompleteBorder={showIncompleteBorder}
              setShowIncompleteBorder={setShowIncompleteBorder}
              postStatus={status}
            />
          ))}
          {!isDisabled && (
            <PlaceholderTile
              id={`placeholder-tile-${uuid}`}
              onClick={() => {
                dispatch(
                  setScrollPosition({
                    page: 'edit',
                    scrollPosition: window.scrollY,
                  }),
                );
                const isInPlanPage = window.location.pathname.includes('/plan');
                const prefix = isInPlanPage ? '/campaigns' : '';
                history.push(
                  `${prefix}/${campaignId}/tile/new?post_id=${uuid}&position=${newTilePosition}`,
                );
              }}
            >
              <Icon className="fa-solid fa-plus" />
              <Text
                fontSize="14px"
                fontWeight="600"
                color="primary.100"
                variant="small3"
              >
                {t('placeholderTile')}
              </Text>
            </PlaceholderTile>
          )}
        </TilesArea>
      </SortableContext>
    </Container>
  );
});

Post.displayName = 'Post';

const Icon = styled.i`
  color: ${themeGet('colors.primary.100')};
`;

const CalendarIcon = styled.i.attrs({
  className: 'fa-light fa-calendar',
})`
  margin-left: 30px;
  color: ${themeGet('colors.black.80')};
`;

const PostDate = styled.div`
  padding: 10px;
  border-radius: 8px;
  background: var(--Black-5, #f9f9fa);
  cursor: pointer;
  border: ${props => (props.$error ? '1px solid #ED1A3A' : 'none')};
`;

const PostStatus = styled.div`
  padding: 10px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const PostText = styled(Text).attrs({
  variant: 'subtitle2',
})`
  color: ${props => (props.$error ? '#ED1A3A' : '#52575C')};
`;

const TileCount = styled(PostText)`
  white-space: nowrap;
`;

const TileDate = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  margin: 0 4px 0 8px;
  text-align: left;
  color: ${themeGet('colors.black.100')};
`;

const Tiles = styled.div`
  border-top: 1px ${themeGet('colors.black.10')} solid;
  width: 100%;
  padding: 20px;
  height: 100%;
  box-shadow: ${({ isOver }) => (isOver ? 'inset 0 0 10px #99CBFF' : 'none')};
  transition: box-shadow 0.5s;
  display: grid;
  grid-template-columns: repeat(auto-fill, 190px);
  column-gap: 30px;
  row-gap: 20px;
`;

const Container = styled.div`
  background: #fff;
  border: 1px ${themeGet('colors.black.10')} solid;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0;
`;

const Header = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  flex-direction: column;
  margin: 0 20px;
`;

const DateStatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ErrorContainer = styled.div`
  text-align: left;
  width: 100%;
  margin-top: 8px;
`;

const ErrorText = styled(Text).attrs({
  variant: 'small2',
})`
  color: ${themeGet('colors.red')};
`;

const PlaceholderTile = styled.div`
  width: 190px;
  height: 218px;
  align-self: center;
  border: 1px ${themeGet('colors.black.10')} solid;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  gap: 10px;
`;
